// biome-ignore-all assist/source/organizeImports: check-ui-types.sh and forms-boundary.test.ts pin these @mantine/form statements
/*
 * The form API is a recorded carve-out (SC-3176): callers keep Mantine's form contract through ~/ui.
 * `zodResolver` and `FormErrors` are re-exported as they are. `useForm` is Mantine's hook with the members
 * whose Mantine 6 meaning Mantine 7 changed owned here (SC-2703): `getInputProps` (Mantine 7 made `value`
 * optional, while the app's components, written against Mantine 6, require it), `setFieldValue` and the
 * `getInputProps` change handler (Mantine 7 records a dirty flag, which stops `isDirty()` seeing `setValues`),
 * and `reset`/`onReset` (Mantine 7 resets to a snapshot `resetDirty()` moves). Their types stay Mantine's; only
 * `getInputProps` needs an owned type. The three vendor statements this file's declaration emits are recorded
 * byte for byte in scripts/check-ui-types.sh; any other vendor reference fails that gate.
 */
import { useCallback, useRef } from "react";

import { useForm as useMantineForm } from "@mantine/form";

import type { UseFormInput, UseFormReturnType as MantineUseFormReturnType } from "@mantine/form";

export { zodResolver } from "@mantine/form";
export type { FormErrors } from "@mantine/form";

type TransformValuesOf<Values> = (values: Values) => unknown;

/** Mantine 7's getInputProps payload: every key optional, since which keys it sets depends on the options. */
type MantineInputProps = ReturnType<MantineUseFormReturnType<object>["getInputProps"]>;
/** Mantine 7's getInputProps options: `type` ("input" or "checkbox"), `withError`, `withFocus`, and enhancer keys. */
type MantineInputOptions = NonNullable<Parameters<MantineUseFormReturnType<object>["getInputProps"]>[1]>;
/** A form path as Mantine types it: a key of the values, or any string. */
type FormPath<Values> = Parameters<MantineUseFormReturnType<Values>["getInputProps"]>[0];

/**
 * Mantine 6's payload contract, the one every call site was written against (@mantine/form 6.0.19
 * lib/types.d.ts:32-43): `value` required, `checked` optional, every other key as Mantine 7 declares it.
 * Both versions set exactly one of the two at runtime (6.0.19 esm/use-form.js:140-144, 7.17.8
 * esm/use-form.mjs:126-136): `checked` for a `{ type: "checkbox" }` read, `value` otherwise. So `value` is
 * undefined on a checkbox read, exactly as it was under Mantine 6's required `value`; this is parity with
 * the shipped type, not a new promise, and checkbox call sites read `checked`.
 */
type FormInputProps = Omit<MantineInputProps, "value"> & Required<Pick<MantineInputProps, "value">>;

/** Mantine 6's single getInputProps signature: options optional, `type` taking either kind. */
type GetInputProps<Values> = (path: FormPath<Values>, options?: MantineInputOptions) => FormInputProps;

// forms-boundary.test.ts pins this exact statement text, so Prettier must not re-break it.
// prettier-ignore
export interface UseFormReturnType<Values, TransformValues extends TransformValuesOf<Values> = (values: Values) => Values>
  extends Omit<MantineUseFormReturnType<Values, TransformValues>, "getInputProps"> {
  getInputProps: GetInputProps<Values>;
}

/**
 * Mantine's useForm, always controlled: Mantine 7's default and the only mode Mantine 6 had, so the payload
 * carries `value`/`checked` rather than `defaultValue`/`defaultChecked`. Every other member is Mantine's own.
 */
export function useForm<
  Values extends object = Record<string, unknown>,
  TransformValues extends TransformValuesOf<Values> = (values: Values) => Values,
>(input?: Omit<UseFormInput<Values, TransformValues>, "mode">): UseFormReturnType<Values, TransformValues> {
  const form = useMantineForm<Values, TransformValues>({ ...input, mode: "controlled" });
  // The values the form was created with, which Mantine 6's reset always restored.
  const initialValues = useRef(form.values);
  /*
   * Mantine 6's setFieldValue, reset and onReset kept one identity for the form's life (6.0.19 esm/use-form.js:64,88,166
   * are useCallback with no deps), and callers list them in effect deps on that promise. The owned ones read the latest
   * form through this ref so they can keep it; Mantine 7's own setFieldValue changes with its `rules` input.
   */
  const formRef = useRef(form);
  formRef.current = form;

  /*
   * Mantine 6's setFieldValue cleared the field's dirty flag (6.0.19 esm/use-form.js:80-90), so a whole-form
   * isDirty() kept comparing values with the snapshot. Mantine 7's writes one instead (7.17.8 esm/use-form.mjs:75),
   * and while any flag exists isDirty() reads the flags only, missing setValues and list changes. Removing the
   * path's flag after Mantine 7 has written it leaves the flags as Mantine 6 left them; isDirty() itself is unchanged.
   */
  const clearFieldDirty = useCallback(
    (path: PropertyKey) =>
      formRef.current.setDirty(current => {
        const field = String(path);
        return field in current ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== field)) : current;
      }),
    [],
  );

  const setFieldValue = useCallback<MantineUseFormReturnType<Values, TransformValues>["setFieldValue"]>(
    (path, value, options) => {
      formRef.current.setFieldValue(path, value, options);
      clearFieldDirty(path);
    },
    [clearFieldDirty],
  );

  const getInputProps: GetInputProps<Values> = (path, options) => {
    const inputProps = form.getInputProps(path, options);
    return {
      ...inputProps,
      // Restates the `value` key Mantine 6's type always promised; `checked` flows through from the payload.
      value: inputProps.value,
      // Mantine 7's handler runs its internal setFieldValue, which writes the flag the owned one clears.
      onChange: (...args: unknown[]) => {
        inputProps.onChange(...args);
        clearFieldDirty(path);
      },
    };
  };

  /*
   * Mantine 6 reset to the values the form was created with (6.0.19 esm/use-form.js:64-70). Mantine 7 resets to
   * its values snapshot (7.17.8 esm/hooks/use-form-values/use-form-values.mjs:61-67), which resetDirty() moves to
   * the current values, so after an applied filter "Clear All" restored that filter. Pointing the snapshot back at
   * the creation values first restores Mantine 6's reset; Mantine 7's own reset then clears errors, dirty and touched.
   */
  const reset = useCallback<MantineUseFormReturnType<Values, TransformValues>["reset"]>(() => {
    formRef.current.setInitialValues(initialValues.current);
    formRef.current.reset();
  }, []);
  const onReset = useCallback<MantineUseFormReturnType<Values, TransformValues>["onReset"]>(
    event => {
      event.preventDefault();
      reset();
    },
    [reset],
  );

  return { ...form, getInputProps, setFieldValue, reset, onReset };
}
